import {
  Card,
  openConfirmDialog,
  openDialog,
  openSuccessDialog,
  ProgressBar,
  Table,
  TableRow,
  closeCurrentDialog,
  Button,
  openDeleteDialog,
} from 'platform/components';
import {Box, Heading, Hide, HStack, Spinner, Text, VStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  ChecklistResponseBody,
  ContextTarget,
  useChangeChecklistMutation,
  useDeleteChecklistMutation,
  useMarkTaskDoneMutation,
  useMarkTaskUndoneMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {openFile} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {ChecklistResourceType} from '../types/ChecklistResourceType';
import {TableCellType} from '../types/TableCellType';
import {CommentCell} from './CommentCell';
import {DocumentCell} from './DocumentCell';
import {EditTaskDialog} from './EditTaskDialog';
import {TableHeaderCell} from './TableHeaderCell';
import {TaskNameCell} from './TaskNameCell';
import {TemplateChooser} from './TemplateChooser';
import {UserCell} from './UserCell';

interface OverviewProps {
  recordId: string;
  resourceId: keyof typeof ChecklistResourceType;
  contextTarget?: ContextTarget;
  contextId?: string;
  data: ChecklistResponseBody;
  isUpdating: boolean;
  isReadOnly?: boolean;
}

export function Overview(props: OverviewProps) {
  const [changeChecklist, changeChecklistQuery] = useChangeChecklistMutation();

  const [markTaskDone, markTaskDoneQuery] = useMarkTaskDoneMutation();
  const [markTaskUndone, markTaskUndoneQuery] = useMarkTaskUndoneMutation();

  const [deleteChecklist, deleteChecklistQuery] = useDeleteChecklistMutation();

  const isUpdating =
    props.isUpdating || markTaskDoneQuery.isLoading || markTaskUndoneQuery.isLoading;

  const handleDeleteChecklist = async () =>
    await deleteChecklist({
      checklistId: props.data.id,
    })
      .unwrap()
      .catch(handleApiError);

  const handleTaskCompletion = async (
    resourceId: string,
    recordId: string,
    checklistId: string,
    taskId: string
  ) => {
    const mandatoryTasks = props.data.tasks.filter((task) => task.isMandatory);
    const completedMandatoryTasks = mandatoryTasks.filter((task) => task.markedAsDone);
    const currentTask = props.data.tasks.find((task) => task.id === taskId);

    const willCompleteAllTasks =
      currentTask?.isMandatory &&
      !currentTask.markedAsDone &&
      completedMandatoryTasks.length === mandatoryTasks.length - 1;

    await markTaskDone({
      resourceId,
      recordId,
      checklistId,
      taskId,
    })
      .unwrap()
      .then(() => {
        if (willCompleteAllTasks) {
          openSuccessDialog({
            id: 'checklist-success-dialog',
            heading: i18n.t('entity.checklist.notifications.completed'),
            subheading: i18n.t('entity.checklist.notifications.completedSub'),
            'data-testid': 'checklist-success-dialog',
            buttonLabel: i18n.t('general.actions.close'),
            onClose: closeCurrentDialog,
          });
        }
      })
      .catch(handleApiError);
  };

  return (
    <Box maxWidth={400} data-testid={testIds.vehicles.checklists('checklistComponent')}>
      <Card>
        <VStack spacing={4}>
          <HStack justify="space-between" align="center">
            <HStack spacing={4} align="center">
              <Heading size={3} data-testid={testIds.vehicles.checklists('checklistTitle-title')}>
                {props.data.name}
              </Heading>
              <Show when={isUpdating}>
                <Spinner size="small" />
              </Show>
            </HStack>

            <Hide when={props.isReadOnly}>
              <HStack spacing={4}>
                <Button
                  leftIcon="action/delete"
                  title={i18n.t('entity.checklist.labels.deleteChecklist')}
                  variant="dangerLink"
                  onClick={() =>
                    openDeleteDialog({
                      text: i18n.t('entity.checklist.labels.deleteChecklistDescription'),
                      onConfirm: handleDeleteChecklist,
                    })
                  }
                  isLoading={deleteChecklistQuery.isLoading}
                />

                <TemplateChooser
                  onTemplateSelected={(id: string) => {
                    openConfirmDialog({
                      text: i18n.t('entity.checklist.labels.confirmChangeTemplate'),
                      onConfirm: () =>
                        changeChecklist({
                          recordId: props.recordId,
                          resourceId: props.resourceId,
                          body: {
                            definitionId: id,
                          },
                        })
                          .unwrap()
                          .catch(handleApiError),
                    });
                  }}
                  isLoading={changeChecklistQuery.isLoading}
                  resourceId={props.resourceId}
                  variant="ghostLink"
                  forUpdate
                />
              </HStack>
            </Hide>
          </HStack>
          <Box maxWidth={200}>
            <Text size="small" color="tertiary">
              {props.data.description}
            </Text>
          </Box>
          <VStack spacing={2}>
            <HStack justify="space-between" align="center">
              <Heading size={4}>{i18n.t('general.labels.completed')}</Heading>
              <Text color="tertiary" data-testid={testIds.vehicles.checklists('progress')}>
                {i18n.t('entity.task.labels.tasks')} {props.data.progress.doneCount}/
                {props.data.progress.totalCount}
              </Text>
            </HStack>
            <Box backgroundColor="palettes.neutral.40.100">
              <ProgressBar percentage={props.data.progress.progress} severity="success" />
            </Box>
          </VStack>
          <Table
            columns={[
              {
                element: <TableHeaderCell label={i18n.t('entity.checklist.labels.taskName')} />,
              },
              {
                element: <TableHeaderCell label={i18n.t('entity.checklist.labels.finishedBy')} />,
              },
              {
                element: <TableHeaderCell label={i18n.t('entity.document.labels.document')} />,
              },
              {
                element: <TableHeaderCell label={i18n.t('entity.checklist.labels.comment')} />,
              },
              {
                element: <TableHeaderCell />,
                width: 12,
              },
            ]}
            variant="bordered"
            tableLayout="fixed"
          >
            {props.data.tasks.map((item) => {
              const hasFile = item.files.length > 0;

              const openEditDialog = (highlightFileUpload?: boolean) => {
                openDialog(
                  <EditTaskDialog
                    dialogId="edit-task-dialog"
                    taskName={item.description}
                    recordId={props.recordId}
                    contextTarget={props.contextTarget}
                    contextId={props.contextId}
                    resourceId={props.resourceId}
                    checklistId={props.data.id}
                    taskId={item.id}
                    note={item.note}
                    files={item.files.map((file) => ({
                      id: file.id,
                      name: file.originalFilename,
                      contentType: file.contentType ?? '',
                      url: file.fileUri,
                    }))}
                    fileRequired={item.fileRequired}
                    highlightFileUpload={highlightFileUpload}
                  />,
                  {
                    size: 'default',
                    id: 'edit-task-dialog',
                  }
                );
              };

              return (
                <TableRow
                  key={item.id}
                  data-testid={testIds.vehicles.checklists('taskRow')}
                  onRowClick={
                    props.isReadOnly
                      ? undefined
                      : async () => {
                          if (item.fileRequired && !hasFile) {
                            openEditDialog(true);
                            return;
                          }

                          if (item.markedAsDone) {
                            return await markTaskUndone({
                              resourceId: props.resourceId,
                              recordId: props.recordId,
                              checklistId: props.data.id,
                              taskId: item.id,
                            })
                              .unwrap()
                              .catch(handleApiError);
                          }

                          return await handleTaskCompletion(
                            props.resourceId,
                            props.recordId,
                            props.data.id,
                            item.id
                          );
                        }
                  }
                  onCellClick={(event, cell) => {
                    if (!cell) {
                      return;
                    }

                    match(cell.cellType as TableCellType)
                      .with('document', () => {
                        const firstFile = item.files[0];
                        if (!firstFile) {
                          return;
                        }

                        event?.preventDefault();
                        event?.stopPropagation();

                        match(item.files.length)
                          .with(1, () => openFile(firstFile.fileUri))
                          .otherwise(() => openEditDialog());
                      })
                      .otherwise(() => {});
                  }}
                  actions={
                    props.isReadOnly
                      ? undefined
                      : {
                          primary: [
                            {
                              icon: 'editor/mode',
                              title: i18n.t('general.labels.edit'),
                              onClick: openEditDialog,
                            },
                          ],
                        }
                  }
                >
                  <TaskNameCell
                    cellType="taskName"
                    recordId={props.recordId}
                    resourceId={props.resourceId}
                    checklistId={props.data.id}
                    taskId={item.id}
                    name={item.description}
                    isMandatory={item.isMandatory}
                    markedAsDone={item.markedAsDone}
                  />
                  <UserCell
                    cellType="finishedBy"
                    userId={item.user?.id}
                    finishedAt={item.finishedAt}
                  />
                  <DocumentCell
                    cellType="document"
                    cellHoverDisabled={hasFile}
                    fileRequired={item.fileRequired}
                    files={item.files.map((file) => ({
                      id: file.id,
                      name: file.originalFilename,
                    }))}
                  />
                  <CommentCell cellType="comment" comment={item.note} />
                </TableRow>
              );
            })}
          </Table>
        </VStack>
      </Card>
    </Box>
  );
}
